import { probe } from '@geo-admin/shared/contract/api/index.ts';
import { CHRONIC_PAUSE_STREAK, type WORKER_AUTH_KINDS } from '@geo-admin/shared/contract/probe-values.ts';
import type { RouteResponse } from '@geo-admin/shared/contract/route.ts';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { DataTable } from '../components/data-table.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody } from '../components/ui/card.tsx';
import { Dialog } from '../components/ui/dialog.tsx';
import { FieldError, Input, Label, Select } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime, relative } from '../lib/format.ts';
import { PageTitle } from '../components/PageTitle.tsx';

type Worker = RouteResponse<typeof probe.listWorkers>['workers'][number];

export function WorkersPage() {
  const workers = useApi(probe.listWorkers, {}, { refetchInterval: 15_000 });
  const update = useApiMutation(probe.updateWorker);
  const pause = useApiMutation(probe.pauseWorker);
  const resume = useApiMutation(probe.resumeWorker);
  const del = useApiMutation(probe.deleteWorker);
  const updateLane = useApiMutation(probe.updateWorkerLane);
  const resumeLane = useApiMutation(probe.resumeWorkerLane);
  const [creating, setCreating] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [install, setInstall] = useState<string | null>(null);

  const columns: ColumnDef<Worker, unknown>[] = [
    {
      header: '워커',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.name}</div>
          <div className="text-xs text-slate-500">
            {row.original.authKind === 'google-oidc' ? row.original.saEmail : '토큰'}
            {row.original.enrollTokenName ? ` · 등록: ${row.original.enrollTokenName}` : ''}
          </div>
        </div>
      ),
    },
    {
      header: '상태',
      id: 'state',
      cell: ({ row }) => {
        const w = row.original;
        return (
          <div className="flex flex-wrap gap-1">
            {w.alive ? <Badge tone="green">살아 있음</Badge> : <Badge tone="red">응답 없음</Badge>}
            {w.enabled ? null : <Badge>꺼짐</Badge>}
            {w.pausedUntil ? (
              <Badge tone="amber" title="정지는 사유와 무관하게 30분입니다. 풀리면 스스로 한 건 탐침합니다">
                정지 ~{dateTime(w.pausedUntil)}
              </Badge>
            ) : null}
            {w.pauseReason && !w.pausedUntil ? (
              <Badge tone="amber" title="탐침 중입니다. 정상 샘플 2건이 연속으로 와야 회복으로 봅니다">
                재개 관찰 중
              </Badge>
            ) : null}
            {w.pauseStreak >= CHRONIC_PAUSE_STREAK ? (
              <Badge tone="red" title="30분 탐침이 계속 실패하고 있습니다. 회선·IP 배치를 확인하세요">
                만성 정지 {w.pauseStreak}회
              </Badge>
            ) : null}
            {w.bootConflictAt ? (
              <Badge tone="red" title={`${dateTime(w.bootConflictAt)} — seal 없이 복제한 기계일 수 있습니다`}>
                자격증명 중복 의심
              </Badge>
            ) : null}
          </div>
        );
      },
    },
    {
      header: '네트워크',
      id: 'net',
      cell: ({ row }) => {
        const w = row.original;
        return (
          <div className="text-xs">
            <div>
              {w.observedIp ?? '—'} <span className="text-slate-400">{w.network}</span>
            </div>
            {w.networkPeers > 0 ? (
              <Badge tone={w.networkLeader ? 'blue' : 'amber'}>
                {w.networkLeader
                  ? `리더 · 같은 네트워크 ${w.networkPeers}대 대기`
                  : '같은 네트워크 리더가 수집 중 — 대기'}
              </Badge>
            ) : null}
            {w.runtime?.reportedEgress?.ip && w.observedIp && w.runtime.reportedEgress.ip !== w.observedIp ? (
              <Badge tone="red">워커가 본 IP {w.runtime.reportedEgress.ip} ≠ 서비스가 본 IP</Badge>
            ) : null}
          </div>
        );
      },
    },
    { header: '간격', accessorKey: 'minGapSec', cell: ({ row }) => `${row.original.minGapSec}초` },
    {
      // 같은 워커의 두 번째 레인 — 정지·간격이 ChatGPT 와 따로다 (261104-gemini-concurrent-lane)
      header: 'Gemini',
      id: 'gemini',
      cell: ({ row }) => {
        const w = row.original;
        const lane = w.lanes.find((l) => l.engine === 'gemini-web');
        const on = lane?.enabled ?? false;
        return (
          <div className="flex flex-col items-start gap-1 text-xs">
            <div className="flex flex-wrap gap-1">
              {on ? <Badge tone="green">켜짐 · {lane!.minGapSec}초</Badge> : <Badge>꺼짐</Badge>}
              {lane?.pausedUntil ? <Badge tone="amber">정지 ~{dateTime(lane.pausedUntil)}</Badge> : null}
              {lane?.pauseReason && !lane.pausedUntil ? <Badge tone="amber">재개 관찰 중</Badge> : null}
              {lane && lane.pauseStreak >= CHRONIC_PAUSE_STREAK ? (
                <Badge tone="red">만성 정지 {lane.pauseStreak}회</Badge>
              ) : null}
            </div>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="outline"
                title="워커 이미지에 Gemini 측정기가 있어야 수집한다. 워커가 꺼져 있으면 레인도 돌지 않는다"
                onClick={() =>
                  updateLane.mutate(
                    { params: { id: w.id, engine: 'gemini-web' }, body: { enabled: !on } },
                    { onError: (e) => alert(e.message) },
                  )
                }
              >
                {on ? '끄기' : '켜기'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const v = prompt('Gemini 시작 간격(초)', String(lane?.minGapSec ?? 374));
                  if (v)
                    updateLane.mutate(
                      { params: { id: w.id, engine: 'gemini-web' }, body: { minGapSec: Number(v) } },
                      { onError: (e) => alert(e.message) },
                    );
                }}
              >
                간격
              </Button>
              {lane?.pausedUntil || lane?.pauseReason ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => resumeLane.mutate({ params: { id: w.id, engine: 'gemini-web' } })}
                >
                  재개
                </Button>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      header: '기계',
      id: 'resources',
      cell: ({ row }) => {
        const r = row.original.runtime?.resources;
        if (!r) return <span className="text-slate-400">—</span>;
        const pct = Math.round((r.memUsedMB / Math.max(1, r.memTotalMB)) * 100);
        return (
          <div className="text-xs">
            <div>
              메모리 {(r.memUsedMB / 1024).toFixed(1)}/{(r.memTotalMB / 1024).toFixed(1)}GB{' '}
              {pct >= 85 ? <Badge tone="red">{pct}%</Badge> : <span className="text-slate-500">{pct}%</span>}
            </div>
            <div className="text-slate-500">
              vCPU {r.cpuCount} · load {r.load1.toFixed(2)}
              {r.load1 > r.cpuCount ? <Badge tone="amber">과부하</Badge> : null}
            </div>
          </div>
        );
      },
    },
    {
      header: '진행 중',
      id: 'work',
      cell: ({ row }) =>
        row.original.currentWork ? (
          <span className="text-xs">
            {row.original.currentWork.queryText} · {relative(row.original.currentWork.leasedAt)}
          </span>
        ) : (
          '—'
        ),
    },
    { header: '마지막 신호', accessorKey: 'lastSeenAt', cell: ({ row }) => relative(row.original.lastSeenAt) },
    {
      header: '',
      id: 'actions',
      cell: ({ row }) => {
        const w = row.original;
        return (
          <div className="flex flex-wrap justify-end gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => update.mutate({ params: { id: w.id }, body: { enabled: !w.enabled } })}
            >
              {w.enabled ? '끄기' : '켜기'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const v = prompt('시작 간격(초) — 실측 없이 374초보다 줄이지 않는다', String(w.minGapSec));
                if (v)
                  update.mutate(
                    { params: { id: w.id }, body: { minGapSec: Number(v) } },
                    { onError: (e) => alert(e.message) },
                  );
              }}
            >
              간격
            </Button>
            {w.pausedUntil || w.pauseReason ? (
              <Button size="sm" variant="outline" onClick={() => resume.mutate({ params: { id: w.id } })}>
                재개
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const h = prompt('몇 시간 정지할까', '2');
                  if (h) pause.mutate({ params: { id: w.id }, body: { hours: Number(h), reason: '수동' } });
                }}
              >
                정지
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                confirm(`${w.name} 을(를) 폐기합니다. 자격증명이 무효가 되고 점유가 회수됩니다`) &&
                del.mutate({ params: { id: w.id } })
              }
            >
              폐기
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageTitle
        title="워커"
        description="네트워크(/24·/64)마다 한 대만 수집한다. 새로 발급한 워커는 꺼져 있다 — 관측 IP 를 확인한 뒤 켠다"
        actions={<Button onClick={() => setCreating(true)}>워커 발급</Button>}
      />
      {workers.data?.workers
        .filter((w) => w.pauseReason)
        .map((w) => (
          <Card key={w.id} className="border-amber-300 bg-amber-50">
            <CardBody className="text-sm text-amber-900">
              <b>{w.name}</b> 정지 사유: {w.pauseReason}
              {w.pauseStreak > 1 ? ` (연속 ${w.pauseStreak}회)` : ''}
            </CardBody>
          </Card>
        ))}
      {workers.data?.workers.flatMap((w) =>
        w.lanes
          .filter((l) => l.pauseReason)
          .map((l) => (
            <Card key={`${w.id}-${l.engine}`} className="border-amber-300 bg-amber-50">
              <CardBody className="text-sm text-amber-900">
                <b>{w.name}</b> · Gemini 정지 사유: {l.pauseReason}
                {l.pauseStreak > 1 ? ` (연속 ${l.pauseStreak}회)` : ''}
              </CardBody>
            </Card>
          )),
      )}
      <Card>
        {workers.isLoading ? (
          <Loading />
        ) : workers.error ? (
          <ErrorBox error={workers.error} />
        ) : (
          <DataTable columns={columns} data={workers.data?.workers ?? []} empty="워커가 없습니다" />
        )}
      </Card>
      <EnrollTokensCard onInstall={setInstall} />
      <CreateWorkerDialog open={creating} onClose={() => setCreating(false)} onToken={setToken} />
      <Dialog open={install !== null} onClose={() => setInstall(null)} title="등록 토큰 — 지금 한 번만 보인다">
        <div className="space-y-2 text-sm">
          <p>
            워커를 띄울 기계에서 아래 한 줄을 실행합니다. 그 기계가 <b>자기 워커와 토큰을 스스로 발급받아</b>
            설치·기동·자동 갱신까지 끝냅니다. 이 창을 닫으면 다시 볼 수 없습니다.
          </p>
          <pre className="overflow-x-auto rounded bg-slate-900 p-3 text-xs text-slate-100">{install}</pre>
          <p className="text-slate-500">
            템플릿·골든 이미지를 만들 때는 <code>probe-worker.sh seal</code> 로 신원을 지운 뒤 굽습니다 — 복제본이 각자
            등록합니다.
          </p>
        </div>
      </Dialog>
      <Dialog open={token !== null} onClose={() => setToken(null)} title="워커 토큰 — 지금 한 번만 보인다">
        <div className="space-y-2 text-sm">
          <p>
            워커 머신의 토큰 파일에 저장한다(--token-file). 이 화면을 닫으면 다시 볼 수 없다 — 잃어버리면 폐기하고 새로
            발급한다.
          </p>
          <pre className="overflow-x-auto rounded bg-slate-900 p-3 text-slate-100">{token}</pre>
        </div>
      </Dialog>
    </>
  );
}

function CreateWorkerDialog({
  open,
  onClose,
  onToken,
}: {
  open: boolean;
  onClose: () => void;
  onToken: (t: string) => void;
}) {
  const create = useApiMutation(probe.createWorker);
  const [kind, setKind] = useState<(typeof WORKER_AUTH_KINDS)[number]>('google-oidc');
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const f = new FormData(e.currentTarget);
    try {
      const r = await create.mutateAsync({
        body: {
          name: String(f.get('name')),
          authKind: kind,
          saEmail: kind === 'google-oidc' ? String(f.get('saEmail')) : null,
        },
      });
      onClose();
      if (r.token) onToken(r.token);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} title="워커 발급">
      <form className="grid gap-3" onSubmit={submit}>
        <Label>
          이름
          <Input name="name" placeholder="gce-seoul-1" required />
        </Label>
        <Label>
          자격증명
          <Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="google-oidc">GCE 서비스 계정 (키 파일 없음)</option>
            <option value="token">워커 토큰 (GCP 밖 머신)</option>
          </Select>
        </Label>
        {kind === 'google-oidc' ? (
          <Label>
            서비스 계정 이메일
            <Input name="saEmail" type="email" placeholder="geo-worker@….iam.gserviceaccount.com" required />
          </Label>
        ) : null}
        <FieldError>{error}</FieldError>
        <Button type="submit">발급</Button>
      </form>
    </Dialog>
  );
}

/**
 * 등록 토큰 — 기계가 스스로 워커를 발급받는 자격증명 (260918-worker-self-enrollment).
 * 여기서 발급한 한 줄을 기계에 붙여넣으면 사람 손 없이 워커가 늘어난다.
 */
function EnrollTokensCard({ onInstall }: { onInstall: (cmd: string) => void }) {
  const list = useApi(probe.listEnrollTokens, {});
  const del = useApiMutation(probe.deleteEnrollToken);
  const [creating, setCreating] = useState(false);
  const tokens = list.data?.enrollTokens ?? [];

  return (
    <>
      <PageTitle
        title="등록 토큰"
        description="기계가 스스로 워커를 발급받는 자격증명입니다. 한 토큰으로 여러 대가 등록하고, 기계마다 다른 워커가 생깁니다"
        actions={<Button onClick={() => setCreating(true)}>등록 토큰 발급</Button>}
      />
      <Card>
        {list.isLoading ? (
          <Loading />
        ) : list.error ? (
          <ErrorBox error={list.error} />
        ) : tokens.length === 0 ? (
          <CardBody className="text-sm text-slate-500">등록 토큰이 없습니다</CardBody>
        ) : (
          <CardBody className="grid gap-2">
            {tokens.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-2 border-slate-100 border-b pb-2 text-sm">
                <span className="font-medium">{t.name}</span>
                {t.autoEnable ? <Badge tone="amber">등록 즉시 켜짐</Badge> : <Badge>꺼진 채 등록</Badge>}
                {t.expired ? <Badge tone="red">만료됨</Badge> : null}
                <span className="text-slate-500 text-xs">
                  워커 {t.workers}
                  {t.maxWorkers === null ? '대' : `/${t.maxWorkers}대`} · 만료 {dateTime(t.expiresAt)}
                </span>
                <Button
                  className="ml-auto"
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    confirm(`${t.name} 을(를) 폐기합니다. 이미 만들어진 워커는 그대로 돕니다`) &&
                    del.mutate({ params: { id: t.id } })
                  }
                >
                  폐기
                </Button>
              </div>
            ))}
          </CardBody>
        )}
      </Card>
      <CreateEnrollTokenDialog open={creating} onClose={() => setCreating(false)} onInstall={onInstall} />
    </>
  );
}

function CreateEnrollTokenDialog({
  open,
  onClose,
  onInstall,
}: {
  open: boolean;
  onClose: () => void;
  onInstall: (cmd: string) => void;
}) {
  const create = useApiMutation(probe.createEnrollToken);
  // ★ 기본은 꺼짐. 새 네트워크를 자동으로 켜는 것은 ToS 미해결 방향이다 (RUNBOOK "다른 네트워크" 1번)
  const [autoEnable, setAutoEnable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const f = new FormData(e.currentTarget);
    const max = String(f.get('maxWorkers')).trim();
    try {
      const r = await create.mutateAsync({
        body: {
          name: String(f.get('name')),
          autoEnable,
          maxWorkers: max === '' ? null : Number(max),
          expiresInDays: Number(f.get('expiresInDays')),
        },
      });
      onClose();
      onInstall(`GEO_ENROLL_TOKEN=${r.token} bash probe-worker.sh install --service-url ${r.serviceUrl}`);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} title="등록 토큰 발급">
      <form className="grid gap-3" onSubmit={submit}>
        <Label>
          이름
          <Input name="name" placeholder="주거 회선 데스크탑" required />
        </Label>
        <Label>
          워커 상한 (비우면 무제한)
          <Input name="maxWorkers" type="number" min={1} max={1000} defaultValue={5} />
        </Label>
        <Label>
          유효기간 (일)
          <Input name="expiresInDays" type="number" min={1} max={365} defaultValue={30} required />
        </Label>
        <Label className="flex-row items-center gap-2">
          <input type="checkbox" checked={autoEnable} onChange={(e) => setAutoEnable(e.target.checked)} />
          등록되는 워커를 바로 켠다
        </Label>
        {autoEnable ? (
          <p className="text-amber-700 text-xs">
            새 네트워크를 자동으로 켜면 회선 수만큼 요청이 늘어납니다. 어느 회선에 둘지 확인하고 켜세요.
          </p>
        ) : (
          <p className="text-slate-500 text-xs">꺼진 채로 등록됩니다. 관측 IP 를 확인한 뒤 위 목록에서 켭니다.</p>
        )}
        <FieldError>{error}</FieldError>
        <Button type="submit">발급</Button>
      </form>
    </Dialog>
  );
}
